import { IBonusTransaction, IStore, ITransactionCheck } from "@/shared/api/bonuses";
import { useAppSelector } from "@/shared/lib/redux";
import { Table } from "@/shared/ui/table";
import { ColumnDef } from "@tanstack/react-table";
import { FC, useMemo } from "react";
import { ActionButton } from "@/shared/ui/action-button";
import { useNavigate } from "react-router-dom";
import ArrowLeftSvg from '@/shared/assets/images/ArrowBendDownRight.svg'
import { BonusAmountCard, getOperationType, selectBonuses } from "@/entities/bonus";
import { toLocaleDateTimeString } from "@/shared/lib/date-formatters";

interface BonusTransactionsTableRendererProps {
    transactions: IBonusTransaction[]
    checks: { [key: string]: ITransactionCheck }
    stores: { [key: string]: IStore }
}

export const BonusTransactionsTableRenderer: FC<BonusTransactionsTableRendererProps> = ({ transactions, checks, stores }) => {
    const bonuses = useAppSelector(selectBonuses)
    const navigate = useNavigate()
    const parsedTransactions = useMemo(() => {
        const parsed: IBonusTransaction[] = []
        const reasonIds: string[] = []
        transactions.forEach((transaction) => {
            if (transaction.reason_type === 6) {
                if (reasonIds.includes(transaction.reason_id)) {
                    return
                } else {
                    reasonIds.push(transaction.reason_id)
                }
            }
            parsed.push(transaction)
        })
        return parsed
    }, [transactions])

    const getTransactionBonus = (transaction: IBonusTransaction) => {
        return bonuses.find(bonus => bonus.id === transaction.bonus_id)
    }

    const getLinkedTransaction = (transaction: IBonusTransaction) => {
        return transactions.filter(t => t.reason_id === transaction.reason_id && t.id !== transaction.id).at(-1)
    }

    const columns: ColumnDef<IBonusTransaction, any>[] = [
        {
            accessorFn: (transaction) => {
                const transactionBonus = getTransactionBonus(transaction)
                return (
                    transaction.reason_type === 6
                        ? <div className="flex gap-2 items-center pr-4">
                            {getLinkedTransaction(transaction)
                                ? <div className="relative shrink-0">
                                    <img
                                        className="w-8 h-8 z-10 relative"
                                        src={getTransactionBonus(getLinkedTransaction(transaction))?.image}
                                    />
                                    <img
                                        className="absolute bottom-[-6px] right-[-6px] w-8 h-8"
                                        src={getTransactionBonus(transaction)?.image}
                                    />
                                </div>
                                : <div className="w-8 h-8 rounded-full overflow-hidden">
                                    <img src={transactionBonus?.image} />
                                </div>
                            }
                            <div className="flex flex-col gap-[2px]">
                                <div className="text-base">
                                    {transactionBonus?.symbol}
                                </div>
                                <div className="text-[13px] text-[#828282]">
                                    {transactionBonus?.name}
                                </div>
                            </div>
                        </div>
                        : <div className="flex gap-2 items-center pr-4">
                            <div className='relative shrink-0'>
                                <img
                                    className="w-8 h-8 z-10 relative"
                                    src={transactionBonus?.image}
                                />
                                {Boolean(stores[transaction.store_id]?.logo_url) &&
                                    <img
                                        className="absolute bottom-[-6px] right-[-6px] w-8 h-8"
                                        src={stores[transaction.store_id]?.logo_url as string}
                                    />
                                }
                            </div>
                            <div className="flex flex-col gap-[2px]">
                                <div className="text-base">
                                    {transactionBonus?.symbol}
                                </div>
                                <div className="text-[13px] text-[#828282]">
                                    {transactionBonus?.name}
                                </div>
                            </div>
                        </div>
                )
            },
            enableSorting: false,
            id: 'name',
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Бонус</div>,
        },
        {
            accessorFn: (transaction) => (
                <div className="w-fit uppercase">
                    {transaction.reason_type === 6
                        ? getLinkedTransaction(transaction)
                            ? <BonusAmountCard amountNano={getLinkedTransaction(transaction).amount_nano} />
                            : <BonusAmountCard amountNano={transaction.amount_nano} />
                        : <BonusAmountCard amountNano={transaction.amount_nano} />
                    }
                </div>
            ),
            enableSorting: false,
            id: 'amount',
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Сумма транзакции</div>,
        },
        {
            accessorFn: (transaction) => (
                <div className="w-fit uppercase font-semibold text-nowrap">
                    {transaction.reason_type === 6
                        ? getLinkedTransaction(transaction)
                            ? `Обмен ${bonuses.find(bonus => bonus.id === getLinkedTransaction(transaction)?.bonus_id)?.symbol} на ${bonuses.find(bonus => bonus.id === transaction.bonus_id)?.symbol}`
                            : getOperationType(transaction)
                        : getOperationType(transaction)
                    }
                </div>
            ),
            enableSorting: false,
            id: 'type',
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Тип операции</div>,
        },
        {
            accessorFn: (transaction) => (
                <div className="w-fit">
                    <div className="text-[13px] rounded-full text-nowrap bg-color-primary py-[6px] px-3 font-normal">
                        {transaction.id}
                    </div>
                </div>
            ),
            enableSorting: false,
            id: 'id',
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">ID Операции</div>,
        },
        {
            accessorFn: (transaction) => (
                <div>
                    {toLocaleDateTimeString(transaction.created_date)}
                </div>
            ),
            enableSorting: false,
            id: 'date',
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Дата операции</div>,
        },
        {
            accessorFn: (transaction) => (
                <div className="w-full flex justify-end gap-2">
                    <ActionButton
                        onClick={() => navigate(`/bonuses/transactions/${transaction.id}`)}
                        icon={ArrowLeftSvg}
                    />
                </div>
            ),
            enableSorting: false,
            id: 'actions',
            cell: info => info.getValue(),
            header: () => <div />,
        },
    ]

    return (
        <Table columns={columns} data={parsedTransactions} />
    )
}